import { useEffect } from "react";

// Google-Maps style touch gestures on the canvas viewport:
// 1-finger pan, 2-finger pinch zoom, double-tap zoom-in,
// two-finger-tap zoom-out, one-finger double-tap-drag zoom.
export function useCanvasGestures(vpRef, tRef, apply, onTapRef) {
  useEffect(() => {
    const vp = vpRef.current;
    if (!vp) return;
    const pts = new Map();
    let mode = null;
    let startDist = 1, startZoom = 1, startWorld = null, lastMid = { x: 0, y: 0 };
    let startAngle = 0, startRot = 0, rotStarted = false;
    let panBase = null, panStart = null;
    let downTime = 0, downPos = { x: 0, y: 0 }, maxMove = 0;
    let twoFingerStart = 0, pinchMaxDelta = 0;
    let lastTap = null;
    let ofz = null, ofzMoved = false;

    const clamp = (z) => Math.min(8, Math.max(0.05, z));
    // viewport-relative screen point -> world coords (accounts for current rotation)
    const screenToWorld = (sx, sy) => {
      const tt = tRef.current;
      const dx = (sx - tt.panX) / tt.zoom;
      const dy = (sy - tt.panY) / tt.zoom;
      const a = -(tt.rot || 0) * Math.PI / 180;
      return { x: dx * Math.cos(a) - dy * Math.sin(a), y: dx * Math.sin(a) + dy * Math.cos(a) };
    };
    const zoomAround = (sx, sy, factor) => {
      const tt = tRef.current;
      const wx = (sx - tt.panX) / tt.zoom, wy = (sy - tt.panY) / tt.zoom;
      const nz = clamp(tt.zoom * factor);
      apply({ zoom: nz, panX: sx - wx * nz, panY: sy - wy * nz });
    };
    const zoomAroundFixed = (() => {
      let anchorWorld = null, anchorAx = null, anchorAy = null;
      return (ax, ay, nz) => {
        if (anchorAx !== ax || anchorAy !== ay) {
          const tt = tRef.current;
          anchorWorld = { x: (ax - tt.panX) / tt.zoom, y: (ay - tt.panY) / tt.zoom };
          anchorAx = ax; anchorAy = ay;
        }
        apply({ zoom: nz, panX: ax - anchorWorld.x * nz, panY: ay - anchorWorld.y * nz });
      };
    })();

    const onDown = (e) => {
      if (e.pointerType !== "touch") return;
      pts.set(e.pointerId, { x: e.clientX, y: e.clientY });
      const now = Date.now();
      const r = vp.getBoundingClientRect();
      if (pts.size === 1) {
        downTime = now; downPos = { x: e.clientX, y: e.clientY }; maxMove = 0;
        const isDouble = lastTap && (now - lastTap.t < 300) &&
          Math.hypot(e.clientX - lastTap.x, e.clientY - lastTap.y) < 30;
        if (isDouble) {
          mode = "onefingerzoom"; ofzMoved = false; lastTap = null;
          const ax = e.clientX - r.left, ay = e.clientY - r.top;
          ofz = { startY: e.clientY, startZoom: tRef.current.zoom, ax, ay };
        } else {
          mode = "pan";
          panBase = { ...tRef.current };
          panStart = { x: e.clientX, y: e.clientY };
        }
      } else if (pts.size === 2) {
        mode = "pinch";
        const a = [...pts.values()];
        startDist = Math.hypot(a[0].x - a[1].x, a[0].y - a[1].y) || 1;
        startZoom = tRef.current.zoom;
        startAngle = Math.atan2(a[0].y - a[1].y, a[0].x - a[1].x);
        startRot = tRef.current.rot || 0;
        rotStarted = false;
        twoFingerStart = now; pinchMaxDelta = 0;
        const midx = (a[0].x + a[1].x) / 2 - r.left, midy = (a[0].y + a[1].y) / 2 - r.top;
        lastMid = { x: midx, y: midy };
        startWorld = screenToWorld(midx, midy);
      }
    };

    const onMove = (e) => {
      if (!pts.has(e.pointerId)) return;
      pts.set(e.pointerId, { x: e.clientX, y: e.clientY });
      const r = vp.getBoundingClientRect();
      if (mode === "pinch" && pts.size >= 2) {
        const a = [...pts.values()];
        const dist = Math.hypot(a[0].x - a[1].x, a[0].y - a[1].y) || 1;
        pinchMaxDelta = Math.max(pinchMaxDelta, Math.abs(dist - startDist));
        const midx = (a[0].x + a[1].x) / 2 - r.left, midy = (a[0].y + a[1].y) / 2 - r.top;
        lastMid = { x: midx, y: midy };
        const nz = clamp(startZoom * (dist / startDist));
        // two-finger twist -> rotation (activates past a small threshold to keep zoom stable)
        const ang = Math.atan2(a[0].y - a[1].y, a[0].x - a[1].x);
        if (!rotStarted && Math.abs(ang - startAngle) > 0.14) {
          rotStarted = true; startAngle = ang; startRot = tRef.current.rot || 0;
        }
        const nr = rotStarted ? startRot + (ang - startAngle) * 180 / Math.PI : (tRef.current.rot || 0);
        const ar = nr * Math.PI / 180, ca = Math.cos(ar), sa = Math.sin(ar);
        const rx = startWorld.x * ca - startWorld.y * sa;
        const ry = startWorld.x * sa + startWorld.y * ca;
        apply({ zoom: nz, rot: nr, panX: midx - rx * nz, panY: midy - ry * nz });
      } else if (mode === "onefingerzoom" && pts.size === 1) {
        if (Math.abs(e.clientY - ofz.startY) > 4) ofzMoved = true;
        const dy = ofz.startY - e.clientY;
        zoomAroundFixed(ofz.ax, ofz.ay, clamp(ofz.startZoom * Math.exp(dy * 0.006)));
      } else if (mode === "pan" && pts.size === 1) {
        maxMove = Math.max(maxMove, Math.hypot(e.clientX - downPos.x, e.clientY - downPos.y));
        apply({ zoom: panBase.zoom, panX: panBase.panX + (e.clientX - panStart.x), panY: panBase.panY + (e.clientY - panStart.y) });
      }
    };

    const onUp = (e) => {
      if (!pts.has(e.pointerId)) return;
      const prevSize = pts.size;
      pts.delete(e.pointerId);
      const now = Date.now();
      if (mode === "onefingerzoom" && pts.size === 0) {
        if (!ofzMoved) zoomAround(ofz.ax, ofz.ay, 2);
        mode = null; ofz = null;
        return;
      }
      if (mode === "pinch" && prevSize === 2) {
        if (now - twoFingerStart < 300 && pinchMaxDelta < 20) {
          zoomAround(lastMid.x, lastMid.y, 0.5);
        }
        if (pts.size === 1) {
          mode = "pan";
          const a = [...pts.values()][0];
          panBase = { ...tRef.current };
          panStart = { x: a.x, y: a.y };
        } else { mode = null; }
        return;
      }
      if (mode === "pan" && prevSize === 1 && pts.size === 0) {
        if (maxMove < 12 && now - downTime < 250) {
          lastTap = { t: now, x: downPos.x, y: downPos.y };
          if (onTapRef && onTapRef.current) onTapRef.current(downPos.x, downPos.y);
        }
        mode = null;
      } else if (pts.size === 0) { mode = null; }
    };

    vp.addEventListener("pointerdown", onDown, { capture: true });
    vp.addEventListener("pointermove", onMove, { capture: true });
    vp.addEventListener("pointerup", onUp, { capture: true });
    vp.addEventListener("pointercancel", onUp, { capture: true });
    return () => {
      vp.removeEventListener("pointerdown", onDown, { capture: true });
      vp.removeEventListener("pointermove", onMove, { capture: true });
      vp.removeEventListener("pointerup", onUp, { capture: true });
      vp.removeEventListener("pointercancel", onUp, { capture: true });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
}
